import { popinApi, dashApi } from '../../../api/api';
import { extractEventId, formatResponseDate, normalizeQuestionType } from './utils';
import { QUESTION_TYPES, SESSION_TYPES, ANALYSIS_THEMES, ANALYSIS_METRICS, CACHE_CONFIG } from './constants';
import { API_BASE_URL } from '../../../config';
import { getCachedInsights, cacheInsights, clearCache, getCacheStats } from '../utils/storage';
import { getPromptForQuestionType } from '../utils/prompts';
import { parseMarkdownResponse } from '../utils/parser';
import { fetchInsightsSuccess, fetchInsightsError } from '../actions/insights';
import Logger from '../../../util/Logger';
import store from '../../../store/configureStore';

// Constants
const MAX_POSTS_PER_BATCH = 50;
const REQUEST_TIMEOUT = 30000; // 30 seconds

// Configuration for OpenAI API
const OPENAI_API_ENDPOINT = process.env.REACT_APP_OPENAI_API_ENDPOINT || 'https://api.openai.com/v1/chat/completions';
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
// Optional proxy to route through POPin-AI-Service (server-side key)
const OPENAI_PROXY_ENDPOINT = process.env.REACT_APP_AI_PROXY_ENDPOINT || null;

// Performance metrics
const performanceMetrics = {
  requests: new Map(),
  cache: {
    hits: 0,
    misses: 0,
    errors: 0
  },
  api: {
    openai: {
      calls: 0,
      errors: 0
    },
    popin: {
      calls: 0,
      errors: 0,
      avgResponseTime: 0
    },
    dash: {
      calls: 0
    }
  }
};

/**
 * Get user context headers for tracking
 * @returns {Object} Headers with user identification
 */
const getUserContextHeaders = () => {
  try {
    const state = store.getState();
    const user = state?.user?.user;
    
    if (user && user.id && user.email) {
       console.log('[AI Insights] Adding user headers:', { userId: user.id, email: user.email });
      return {
        'X-POPin-User-ID': String(user.id),
        'X-POPin-User-Email': user.email
      };
    } else {
       console.log('[AI Insights] No user context available');
    }
  } catch (error) {
     console.log('[AI Insights] Error extracting user context:', error.message);
  }
  return {};
};

// Performance monitoring utility
const measurePerformance = async (operation, name) => {
  const startTime = performance.now();
  const requestId = `${name}_${Date.now()}`;
  performanceMetrics.requests.set(requestId, { startTime });

  try {
    const result = await operation();
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Update metrics
    const metrics = performanceMetrics.requests.get(requestId);
    metrics.duration = duration;
    metrics.success = true;

    // Log performance data
    // console.log(`⏱️ Performance [${name}]:`, {
    //   duration: `${duration.toFixed(2)}ms`,
    //   timestamp: new Date().toISOString()
    // });

    return result;
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Update metrics
    const metrics = performanceMetrics.requests.get(requestId);
    metrics.duration = duration;
    metrics.success = false;
    metrics.error = error;

    // Log error with performance data
    console.error(`❌ Performance Error [${name}]:`, {
      duration: `${duration.toFixed(2)}ms`,
      error: error.message,
      timestamp: new Date().toISOString()
    });

    throw error;
  } finally {
    // Clean up old metrics (keep last 100)
    const metricsArray = Array.from(performanceMetrics.requests.entries());
    if (metricsArray.length > 100) {
      performanceMetrics.requests = new Map(metricsArray.slice(-100));
    }
  }
};

// Debounce configuration
const DEBOUNCE_DELAY = 1000; // 1 second
let lastCallTime = 0;

// API service for handling event insights and analysis
export const AIApi = {
  /**
   * Get AI insights for an event
   * @param {string} eventId - ID of the event
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Promise resolving to insights data
   */
  getInsights: async function(eventId, options = {}) {
    // Ensure eventId is a string
    const eventIdStr = typeof eventId === 'object' ? eventId?.eventId || eventId?.id : eventId;
    
    // console.log('🔍 Getting insights for:', eventIdStr, options);
    
    if (!eventIdStr) {
      const error = new Error('Must provide an eventId');
      console.error('❌ Invalid eventId:', error);
      throw error;
    }

    try {
      // Check cache first
      const cachedData = getCachedInsights(eventIdStr, options);
      if (cachedData) {
        // console.log('📦 Cache hit for:', eventIdStr);
        performanceMetrics.cache.hits++;
        return cachedData;
      }

      performanceMetrics.cache.misses++;
      
      // Fetch required data
      const [eventDetails, posts, stats] = await Promise.all([
        this.getEventDetails(eventIdStr),
        this.getEventPosts(eventIdStr),
        this.getEventStats(eventIdStr)
      ]);

      // Generate insights
      const insights = await this.generateInsights(eventDetails, posts, stats, options);
      
      // Cache the results
      cacheInsights(eventIdStr, insights, options);
      
      return insights;
    } catch (error) {
      console.error('❌ Error getting insights:', error);
      performanceMetrics.cache.errors++;
      throw error;
    }
  },

  /**
   * Generate insights from event data
   * @param {Object} eventDetails - Event details
   * @param {Object} posts - Event posts
   * @param {Object} stats - Event statistics
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Promise resolving to generated insights
   */
  generateInsights: async function(eventDetails, posts, stats, options = {}) {
    // Create debug session ID
    const debugSessionId = `insights_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // console.log('🤖 Starting insights generation:', {
    //   debugSessionId,
    //   eventId: eventDetails.id,
    //   options,
    //   timestamp: new Date().toISOString()
    // });

    // Add detailed logging for stats data
    // console.log('📊 EVENT STATS BEFORE PROMPT GENERATION:', {
    //   debugSessionId,
    //   eventId: eventDetails.id,
    //   hasStats: !!stats,
    //   statsType: typeof stats,
    //   statsKeys: stats ? Object.keys(stats) : [],
    //   hasSummary: !!stats?.summary,
    //   summaryKeys: stats?.summary ? Object.keys(stats.summary) : [],
    //   summaryData: stats?.summary,
    //   hasMetrics: !!stats?.metrics,
    //   metricsKeys: stats?.metrics ? Object.keys(stats.metrics) : [],
    //   timestamp: new Date().toISOString()
    // });

    try {
      // Normalize question type from event details
      const questionType = normalizeQuestionType(eventDetails.question_type);
      // console.log('📝 Question type:', { 
      //   questionType, 
      //   original: eventDetails.question_type,
      //   normalized: questionType,
      //   fromOptions: options.questionType,
      //   eventId: eventDetails.id,
      //   timestamp: new Date().toISOString()
      // });

      // Prepare data for prompt generation
      const promptData = {
        eventDetails: {
          ...eventDetails,
          question_type: questionType
        },
        posts: posts?.posts || [],
        stats
      };

      // Get prompt for question type
      const prompt = getPromptForQuestionType(questionType, promptData);
      // console.log('🤖 Raw Prompt:', {
      //   debugSessionId,
      //   eventId: eventDetails.id,
      //   questionType,
      //   prompt,
      //   timestamp: new Date().toISOString()
      // });

      // Check if prompt is null before proceeding
      if (!prompt) {
        console.error('❌ Cannot generate insights: Prompt is null', {
          debugSessionId,
          eventId: eventDetails.id,
          questionType,
          timestamp: new Date().toISOString()
        });
        throw new Error('Failed to generate prompt for question type: ' + questionType);
      }

      // Get insights from OpenAI
      const response = await measurePerformance(async () => {
        // Make request to OpenAI API (direct) or via proxy (server-side)
        const targetEndpoint = OPENAI_PROXY_ENDPOINT || OPENAI_API_ENDPOINT;
        const headers = {
          'Content-Type': 'application/json',
          ...getUserContextHeaders() // Add user tracking headers
        };
        // Only attach Authorization header when calling OpenAI directly from the browser
        if (!OPENAI_PROXY_ENDPOINT) {
          headers['Authorization'] = `Bearer ${OPENAI_API_KEY}`;
        }

        const result = await fetch(targetEndpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            model: options.model || 'gpt-4',
            messages: [
              { role: 'system', content: 'You are a helpful assistant that analyzes event data.' },
              { role: 'user', content: prompt }
            ],
            temperature: 0.7
          })
        });

        if (!result.ok) {
          throw new Error(`OpenAI API error: ${result.status} ${result.statusText}`);
        }

        const data = await result.json();
        // console.log('🤖 LLM Response:', {
        //   debugSessionId,
        //   eventId: eventDetails.id,
        //   questionType,
        //   rawResponse: data,
        //   content: data.choices[0]?.message?.content,
        //   timestamp: new Date().toISOString()
        // });

        return data;
      }, 'openai_api_call');

      // Parse the markdown response into sections
      const content = response.choices[0].message.content;
      const startTime = new Date().toISOString();
      
      // console.log('🔍 Starting response parsing:', {
      //   debugSessionId,
      //   contentLength: content.length,
      //   timestamp: startTime
      // });

      const sections = parseMarkdownResponse(content);
      
      // console.log('📊 Parsed sections:', {
      //   debugSessionId,
      //   sections,
      //   foundSections: sections._meta.foundSections,
      //   missingSections: sections._meta.missingSections,
      //   parsingDuration: sections._meta.parsing.duration,
      //   timestamp: new Date().toISOString()
      // });

      // Process stats for direct UI consumption based on question type
      let processedStats = stats;
      
      // Extract and format stats based on question type
      switch (questionType) {
        case QUESTION_TYPES.BINARY: {
          const binaryStats = {
            yes: 0,
            no: 0,
            yesPercentage: 0,
            noPercentage: 0,
            totalResponses: 0
          };
          
          // Log the raw stats for debugging
          console.log('📊 Raw stats for binary question:', {
            debugSessionId,
            summary: stats?.summary,
            summaryKeys: stats?.summary ? Object.keys(stats.summary) : [],
            summaryEntries: stats?.summary ? Object.entries(stats.summary) : [],
            timestamp: new Date().toISOString()
          });
          
          // First, check for the binary data in the ideas array of the summary
          // This is the most common format for binary data in this API
          if (stats?.summary?.ideas && Array.isArray(stats.summary.ideas)) {
            console.log('📊 Found ideas array in summary, checking for Yes/No:', {
              debugSessionId,
              ideasLength: stats.summary.ideas.length,
              ideasData: stats.summary.ideas,
              timestamp: new Date().toISOString()
            });
            
            // Look for items with title "Yes" and "No" in the ideas array
            const yesItem = stats.summary.ideas.find(item => 
              item.title && (item.title.toLowerCase() === 'yes' || item.title.toLowerCase() === 'true'));
            const noItem = stats.summary.ideas.find(item => 
              item.title && (item.title.toLowerCase() === 'no' || item.title.toLowerCase() === 'false'));
            
            if (yesItem && yesItem.choice_count !== undefined) {
              binaryStats.yes = parseInt(yesItem.choice_count, 10) || 0;
              // Also check if percentage is available
              if (yesItem.choice_pct) {
                const pctStr = yesItem.choice_pct.replace('%', '');
                binaryStats.yesPercentage = parseInt(pctStr, 10) || 0;
              } else if (yesItem.choice_freq) {
                binaryStats.yesPercentage = Math.round(yesItem.choice_freq * 100);
              }
            }
            
            if (noItem && noItem.choice_count !== undefined) {
              binaryStats.no = parseInt(noItem.choice_count, 10) || 0;
              // Also check if percentage is available
              if (noItem.choice_pct) {
                const pctStr = noItem.choice_pct.replace('%', '');
                binaryStats.noPercentage = parseInt(pctStr, 10) || 0;
              } else if (noItem.choice_freq) {
                binaryStats.noPercentage = Math.round(noItem.choice_freq * 100);
              }
            }
            
            // If we found the data, set totalResponses
            if (binaryStats.yes > 0 || binaryStats.no > 0) {
              binaryStats.totalResponses = binaryStats.yes + binaryStats.no;
              console.log('📊 Successfully extracted binary data from ideas array:', {
                debugSessionId,
                yes: binaryStats.yes,
                no: binaryStats.no,
                yesPercentage: binaryStats.yesPercentage,
                noPercentage: binaryStats.noPercentage,
                totalResponses: binaryStats.totalResponses,
                timestamp: new Date().toISOString()
              });
            }
          }
          
          // If we still don't have data, try the other methods...
          if (binaryStats.yes === 0 && binaryStats.no === 0) {
            // Try to find binary data in other locations
            console.log('📊 Looking for binary data in alternative locations:', {
              debugSessionId,
              statsKeys: Object.keys(stats),
              hasResponses: !!stats.responses,
              responsesKeys: stats.responses ? Object.keys(stats.responses) : [],
              hasResults: !!stats.results,
              resultsKeys: stats.results ? Object.keys(stats.results) : [],
              timestamp: new Date().toISOString()
            });
            
            // Try to extract from stats.responses if available
            if (stats.responses) {
              // Look for yes/no in responses
              Object.entries(stats.responses).forEach(([key, value]) => {
                if (key.toLowerCase().includes('yes') || key.toLowerCase() === 'true') {
                  binaryStats.yes = parseInt(value, 10) || 0;
                } else if (key.toLowerCase().includes('no') || key.toLowerCase() === 'false') {
                  binaryStats.no = parseInt(value, 10) || 0;
                }
              });
            }
            
            // If still no data, try to find it in other possible locations
            if (binaryStats.yes === 0 && binaryStats.no === 0) {
              console.log('📊 Looking for binary data in additional locations:', {
                debugSessionId,
                hasResults: !!stats.results,
                resultsData: stats.results,
                hasData: !!stats.data,
                dataKeys: stats.data ? Object.keys(stats.data) : [],
                timestamp: new Date().toISOString()
              });
              
              // Try stats.results if available
              if (stats.results) {
                Object.entries(stats.results).forEach(([key, value]) => {
                  if (key.toLowerCase().includes('yes') || key.toLowerCase() === 'true') {
                    binaryStats.yes = parseInt(value, 10) || 0;
                  } else if (key.toLowerCase().includes('no') || key.toLowerCase() === 'false') {
                    binaryStats.no = parseInt(value, 10) || 0;
                  }
                });
              }
              
              // Try stats.data if available
              if (stats.data && binaryStats.yes === 0 && binaryStats.no === 0) {
                Object.entries(stats.data).forEach(([key, value]) => {
                  if (key.toLowerCase().includes('yes') || key.toLowerCase() === 'true') {
                    binaryStats.yes = parseInt(value, 10) || 0;
                  } else if (key.toLowerCase().includes('no') || key.toLowerCase() === 'false') {
                    binaryStats.no = parseInt(value, 10) || 0;
                  }
                });
              }
            }
            
          // Calculate total and percentages - do this regardless of data source
          binaryStats.totalResponses = binaryStats.yes + binaryStats.no;
          if (binaryStats.totalResponses > 0) {
            binaryStats.yesPercentage = Math.round((binaryStats.yes / binaryStats.totalResponses) * 100);
            binaryStats.noPercentage = Math.round((binaryStats.no / binaryStats.totalResponses) * 100);
          }
          }
          
          console.log('📊 Extracted binary stats for UI:', {
            debugSessionId,
            binaryStats,
            fromSummary: !!stats?.summary,
            timestamp: new Date().toISOString()
          });
          
          processedStats = {
            ...stats,
            binaryStats
          };
          
          // Log the final processed stats
          console.log('📊 Final processed stats for binary question:', {
            debugSessionId,
            binaryStats,
            processedStatsKeys: Object.keys(processedStats),
            timestamp: new Date().toISOString()
          });
          break;
        }
        
        case QUESTION_TYPES.MULTIPLE_CHOICE: {
          const multipleChoiceStats = {
            options: [],
            totalResponses: 0,
            distribution: {}
          };
          
          // Extract options and their counts from summary
          if (stats?.summary) {
            // Filter out non-option entries like 'total'
            const optionEntries = Object.entries(stats.summary).filter(([key]) => 
              !['total', 'count', 'sum'].includes(key.toLowerCase()));
            
            // Calculate total responses
            multipleChoiceStats.totalResponses = optionEntries.reduce((sum, [_, count]) => 
              sum + (parseInt(count, 10) || 0), 0);
            
            // Format options with percentages
            multipleChoiceStats.options = optionEntries.map(([option, count]) => {
              const numCount = parseInt(count, 10) || 0;
              const percentage = multipleChoiceStats.totalResponses > 0 
                ? Math.round((numCount / multipleChoiceStats.totalResponses) * 100) 
                : 0;
              
              return {
                option,
                count: numCount,
                percentage
              };
            });
            
            // Create distribution object
            multipleChoiceStats.distribution = Object.fromEntries(
              multipleChoiceStats.options.map(item => [item.option, item.count])
            );
          }
          
          console.log('📊 Extracted multiple choice stats for UI:', {
            debugSessionId,
            multipleChoiceStats,
            fromSummary: !!stats?.summary,
            timestamp: new Date().toISOString()
          });
          
          processedStats = {
            ...stats,
            multipleChoiceStats
          };
          break;
        }
        
        case QUESTION_TYPES.SCALE: {
          const scaleStats = {
            distribution: {},
            average: 0,
            median: 0,
            mode: 0,
            min: 0,
            max: 0,
            totalResponses: 0
          };
          
          if (stats?.summary) {
            // Extract scale distribution
            const scaleEntries = Object.entries(stats.summary)
              .filter(([key]) => !isNaN(parseInt(key, 10)));
            
            // Sort by numeric key
            scaleEntries.sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10));
            
            // Calculate total responses
            scaleStats.totalResponses = scaleEntries.reduce((sum, [_, count]) => 
              sum + (parseInt(count, 10) || 0), 0);
            
            // Create distribution object
            scaleStats.distribution = Object.fromEntries(
              scaleEntries.map(([scale, count]) => [scale, parseInt(count, 10) || 0])
            );
            
            // Calculate average if available
            if (scaleEntries.length > 0 && scaleStats.totalResponses > 0) {
              const sum = scaleEntries.reduce((acc, [scale, count]) => 
                acc + (parseInt(scale, 10) * parseInt(count, 10)), 0);
              scaleStats.average = parseFloat((sum / scaleStats.totalResponses).toFixed(1));
              
              // Find min and max
              scaleStats.min = parseInt(scaleEntries[0][0], 10);
              scaleStats.max = parseInt(scaleEntries[scaleEntries.length - 1][0], 10);
              
              // Find mode (most common response)
              let maxCount = 0;
              scaleEntries.forEach(([scale, count]) => {
                const numCount = parseInt(count, 10) || 0;
                if (numCount > maxCount) {
                  maxCount = numCount;
                  scaleStats.mode = parseInt(scale, 10);
                }
              });
              
              // Calculate median (middle value)
              const allValues = [];
              scaleEntries.forEach(([scale, count]) => {
                const numScale = parseInt(scale, 10);
                const numCount = parseInt(count, 10) || 0;
                for (let i = 0; i < numCount; i++) {
                  allValues.push(numScale);
                }
              });
              
              if (allValues.length > 0) {
                allValues.sort((a, b) => a - b);
                const mid = Math.floor(allValues.length / 2);
                scaleStats.median = allValues.length % 2 === 0
                  ? (allValues[mid - 1] + allValues[mid]) / 2
                  : allValues[mid];
              }
            }
          }
          
          console.log('📊 Extracted scale stats for UI:', {
            debugSessionId,
            scaleStats,
            fromSummary: !!stats?.summary,
            timestamp: new Date().toISOString()
          });
          
          processedStats = {
            ...stats,
            scaleStats
          };
          break;
        }
        
        case QUESTION_TYPES.STACK_RANK: {
          const stackRankStats = {
            options: [],
            rankings: {},
            totalResponses: 0
          };
          
          if (stats?.summary) {
            // Extract option names and their rankings
            const optionEntries = Object.entries(stats.summary)
              .filter(([key]) => !['total', 'count', 'sum'].includes(key.toLowerCase()));
            
            // Format options with their rankings
            stackRankStats.options = optionEntries.map(([option, rankData]) => {
              // Parse rank data which might be in format "1:5,2:3,3:2" (position:count)
              const rankings = {};
              let totalRankings = 0;
              
              if (typeof rankData === 'string' && rankData.includes(':')) {
                rankData.split(',').forEach(pair => {
                  const [position, count] = pair.split(':');
                  const numPosition = parseInt(position, 10);
                  const numCount = parseInt(count, 10) || 0;
                  
                  rankings[numPosition] = numCount;
                  totalRankings += numCount;
                });
              }
              
              return {
                option,
                rankings,
                totalRankings
              };
            });
            
            // Calculate total responses
            stackRankStats.totalResponses = stackRankStats.options.length > 0
              ? stackRankStats.options[0].totalRankings
              : 0;
            
            // Create rankings object
            stackRankStats.rankings = Object.fromEntries(
              stackRankStats.options.map(item => [item.option, item.rankings])
            );
          }
          
          console.log('📊 Extracted stack rank stats for UI:', {
            debugSessionId,
            stackRankStats,
            fromSummary: !!stats?.summary,
            timestamp: new Date().toISOString()
          });
          
          processedStats = {
            ...stats,
            stackRankStats
          };
          break;
        }
        
        case QUESTION_TYPES.OPEN_ENDED: {
          const openEndedStats = {
            responseCount: 0,
            averageLength: 0,
            wordFrequency: {},
            sentimentDistribution: {
              positive: 0,
              neutral: 0,
              negative: 0
            }
          };
          
          // Extract open-ended response stats
          if (stats?.metrics) {
            openEndedStats.responseCount = stats.metrics.contributed || 0;
            
            // Extract sentiment if available
            if (stats.metrics.posts_sentiment_positive !== undefined) {
              openEndedStats.sentimentDistribution.positive = stats.metrics.posts_sentiment_positive || 0;
              openEndedStats.sentimentDistribution.neutral = stats.metrics.posts_sentiment_neutral || 0;
              openEndedStats.sentimentDistribution.negative = stats.metrics.posts_sentiment_negative || 0;
            }
          }
          
          // Calculate average length if posts are available
          if (posts?.posts && posts.posts.length > 0) {
            const totalLength = posts.posts.reduce((sum, post) => 
              sum + (post.content?.length || 0), 0);
            openEndedStats.averageLength = posts.posts.length > 0
              ? Math.round(totalLength / posts.posts.length)
              : 0;
          }
          
          console.log('📊 Extracted open-ended stats for UI:', {
            debugSessionId,
            openEndedStats,
            fromSummary: !!stats?.summary,
            fromMetrics: !!stats?.metrics,
            timestamp: new Date().toISOString()
          });
          
          processedStats = {
            ...stats,
            openEndedStats
          };
          break;
        }
        
        default:
          // For other question types, just pass the original stats
          break;
      }
      
      // Construct final result object
      const finalResult = {
        content: sections, // Contains AI-generated content (summary, themes, etc.)
        debugInfo: {
          sessionId: debugSessionId,
          eventId: eventDetails.id,
          questionType,
          prompt: {
            sent: prompt,
            timestamp: startTime
          },
          response: {
            raw: content,
            status: response.status,
            statusText: response.statusText,
            model: options.model || 'gpt-4',
            timestamp: new Date().toISOString()
          },
          parsing: sections._meta,
          cache: {
            hit: false,
            timestamp: new Date().toISOString()
          },
          // Include original metrics and summary in debug info for reference
          originalMetrics: stats?.metrics,
          originalSummary: stats?.summary
        },
        // Include the processed stats under originalStats for the transformer
        originalStats: processedStats,
        // Also include the raw metrics directly for easier access in transformer
        metrics: stats?.metrics,
        // Also include the raw summary directly for easier access
        summary: stats?.summary,
        questionType: questionType // Ensure questionType is at the top level
      };

      // Log the final result being returned
      // console.log('✅ Final insights result ready:', {
      //   debugSessionId,
      //   eventId: eventDetails.id,
      //   questionType,
      //   finalResult
      // });

      return finalResult;
    } catch (error) {
      console.error('❌ Error generating insights:', {
        debugSessionId,
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  },

  /**
   * Get event details by ID
   * @param {string} eventId - ID of the event to fetch
   * @returns {Promise<object>} - Promise resolving to event details
   */
  getEventDetails: async function(eventId) {
    // Ensure eventId is a string
    const eventIdStr = typeof eventId === 'object' ? eventId?.eventId || eventId?.id : eventId;
    
    // console.log('🔍 Getting event details for:', eventIdStr);
    
    if (!eventIdStr) {
      const error = new Error('Must provide an eventId');
      console.error('❌ Invalid eventId:', error);
      throw error;
    }

    try {
      const response = await popinApi.get(`/events/detail/${eventIdStr}`);
      // console.log('EventApi.getDetails response:', response);
      // console.log('Event fields available:', Object.keys(response));
      performanceMetrics.api.popin.calls++;
      return response;
    } catch (error) {
      console.error('❌ Error fetching event details:', error);
      performanceMetrics.api.popin.errors++;
      throw error;
    }
  },

  /**
   * Get all posts for an event
   * @param {string} eventId - ID of the event
   * @returns {Promise<object>} - Promise resolving to posts data
   */
  getEventPosts: async function(eventId) {
    // Ensure eventId is a string
    const eventIdStr = typeof eventId === 'object' ? eventId?.eventId || eventId?.id : eventId;
    
    // console.log('📝 Getting event posts for:', eventIdStr);
    
    if (!eventIdStr) {
      const error = new Error('Must provide an eventId');
      console.error('❌ Invalid eventId:', error);
      throw error;
    }

    try {
      const response = await popinApi.get(`/events/${eventIdStr}/posts?include_children=true`);
      // console.log('EventApi.getPosts response:', response);
      // console.log('Posts fields available:', Object.keys(response));
      if (response.posts) {
        // console.log('First post fields:', Object.keys(response.posts[0] || {}));
      }
      performanceMetrics.api.popin.calls++;
      return response;
    } catch (error) {
      console.error('❌ Error fetching event posts:', error);
      performanceMetrics.api.popin.errors++;
      throw error;
    }
  },

  /**
   * Get stats for an event
   * @param {string} eventId - ID of the event
   * @returns {Promise<Object>} Promise resolving to event stats data
   */
  getEventStats: async function(eventId) {
    // console.log('📊 Getting event stats for:', eventId);
    
    if (!eventId) {
      throw new Error('Must provide an eventId');
    }
    
    try {
      const response = await dashApi.get(`/events/${eventId}/stats`);
      
      // Add more detailed logging for API response
      // console.log('📊 EVENT STATS API RESPONSE:', {
      //   eventId,
      //   hasResponse: !!response,
      //   responseType: typeof response,
      //   responseKeys: response ? Object.keys(response) : [],
      //   hasSummary: !!response?.summary,
      //   summaryKeys: response?.summary ? Object.keys(response.summary) : [],
      //   summaryData: response?.summary,
      //   hasMetrics: !!response?.metrics,
      //   metricsKeys: response?.metrics ? Object.keys(response.metrics) : [],
      //   hasResponses: !!response?.responses,
      //   responsesKeys: response?.responses ? Object.keys(response.responses) : [],
      //   hasResults: !!response?.results,
      //   resultsKeys: response?.results ? Object.keys(response.results) : [],
      //   hasData: !!response?.data,
      //   dataKeys: response?.data ? Object.keys(response.data) : [],
      //   // For binary questions, specifically look for yes/no data
      //   binaryData: {
      //     inSummary: response?.summary ? Object.entries(response.summary).filter(([key]) => 
      //       key.toLowerCase().includes('yes') || key.toLowerCase().includes('no') || 
      //       key.toLowerCase() === 'true' || key.toLowerCase() === 'false'
      //     ) : [],
      //     inResponses: response?.responses ? Object.entries(response.responses).filter(([key]) => 
      //       key.toLowerCase().includes('yes') || key.toLowerCase().includes('no') || 
      //       key.toLowerCase() === 'true' || key.toLowerCase() === 'false'
      //     ) : [],
      //     inResults: response?.results ? Object.entries(response.results).filter(([key]) => 
      //       key.toLowerCase().includes('yes') || key.toLowerCase().includes('no') || 
      //       key.toLowerCase() === 'true' || key.toLowerCase() === 'false'
      //     ) : [],
      //     inData: response?.data ? Object.entries(response.data).filter(([key]) => 
      //       key.toLowerCase().includes('yes') || key.toLowerCase().includes('no') || 
      //       key.toLowerCase() === 'true' || key.toLowerCase() === 'false'
      //     ) : []
      //   },
      //   timestamp: new Date().toISOString()
      // });
      
      return response; // Return the full response, not response.data
    } catch (error) {
      console.error('❌ Error getting event stats:', error);
      // Return empty stats object instead of throwing
      return { summary: {}, metrics: {} };
    }
  },

  /**
   * Get theme analysis for an event
   * @param {string} eventId - ID of the event
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} Promise resolving to theme analysis data
   */
  getThemes: async function(eventId, options = {}) {
    // Ensure eventId is a string
    const eventIdStr = typeof eventId === 'object' ? eventId?.eventId || eventId?.id : eventId;
    
    console.log('AIApi.getThemes called with:', { 
      eventId: eventIdStr, 
      options
    });
    
    if (!eventIdStr) {
      const error = new Error('Must provide an eventId');
      console.error('Invalid eventId:', { 
        error, 
        stack: error.stack
      });
      throw { 
        type: 'VALIDATION_ERROR',
        error
      };
    }
    
    const url = `/events/${eventIdStr}/themes`;
    
    try {
      const response = await popinApi.post(url, options);
      // console.log('AI themes fetched successfully');
      return response;
    } catch (error) {
      console.error('Error fetching AI themes:', {
        error,
        eventId: eventIdStr,
        url,
        stack: error.stack
      });
      throw {
        type: 'FETCH_ERROR',
        error,
        message: 'Failed to fetch AI themes',
        details: {
          eventId: eventIdStr,
          url,
          statusCode: error.response?.status
        }
      };
    }
  },

  /**
   * Save custom prompt template
   * @param {string} eventId - ID of the event
   * @param {Object} template - Template data
   * @returns {Promise<Object>} Promise resolving to saved template
   */
  savePromptTemplate: async function(eventId, template) {
    // Ensure eventId is a string
    const eventIdStr = typeof eventId === 'object' ? eventId?.eventId || eventId?.id : eventId;
    
    console.log('AIApi.savePromptTemplate called with:', { 
      eventId: eventIdStr, 
      template
    });
    
    if (!eventIdStr || !template) {
      const error = new Error('Must provide eventId and template');
      console.error('Invalid parameters:', error);
      throw { 
        type: 'VALIDATION_ERROR',
        error
      };
    }
    
    const url = `/events/${eventIdStr}/templates`;
    
    try {
      const response = await popinApi.post(url, template);
      // console.log('Prompt template saved successfully');
      return response;
    } catch (error) {
      console.error('Error saving prompt template:', {
        error,
        eventId: eventIdStr,
        url,
        stack: error.stack
      });
      throw {
        type: 'FETCH_ERROR',
        error,
        message: 'Failed to save prompt template',
        details: {
          eventId: eventIdStr,
          url,
          statusCode: error.response?.status
        }
      };
    }
  },

  /**
   * Determine the question type for given data
   * @param {string} eventId - ID of the event
   * @param {Object} questionData - Question data to analyze
   * @returns {Promise<string>} Promise resolving to question type
   */
  getQuestionType: async function(eventId, questionData) {
    // Ensure eventId is a string
    const eventIdStr = typeof eventId === 'object' ? eventId?.eventId || eventId?.id : eventId;
    
    try {
      const response = await fetch(`${API_BASE_URL}/ai/question-type`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getUserContextHeaders() // Add user tracking headers
        },
        body: JSON.stringify({
          eventId: eventIdStr,
          questionData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to determine question type');
      }

      const data = await response.json();
      return data.questionType;
    } catch (error) {
      console.error('Error in AIApi.getQuestionType:', {
        error,
        eventId: eventIdStr,
        questionData
      });
      throw error;
    }
  },

  /**
   * Clear insights cache for a specific event or all events
   * @param {string} [eventId] - Optional event ID to clear cache for specific event only
   * @returns {boolean} Success status
   */
  clearInsightsCache: function(eventId) {
    // console.log('🧹 Clearing insights cache:', eventId ? `for event ${eventId}` : 'for all events');
    return clearCache(eventId);
  }
};

/**
 * Generate prompt for AI analysis
 * @param {Object} eventDetails - Event details
 * @param {Array<Object>} posts - Posts to analyze
 * @param {Object} stats - Event statistics
 * @param {Object} options - Additional options
 * @returns {string} Prompt for AI analysis
 */
function generatePrompt(eventDetails, posts, stats, options = {}) {
  console.log('Generating prompt:', { 
    postCount: posts.length,
    questionType: eventDetails.question_type,
    sessionType: eventDetails.session_type
  });

  const { question_type, session_type, name, title, summary } = eventDetails;

  // Build context section
  let prompt = `Analyze the following ${session_type || 'survey'} responses:\n\n`;
  prompt += `Event: ${title || name}\n`;
  if (summary) {
    prompt += `Context: ${summary}\n`;
  }
  prompt += `Question Type: ${question_type}\n\n`;

  // --- Add detailed stats/metrics section based on question type ---
  if (stats) {
    // SCALE & MULTIPLE RATING
    if (
      ['SCALE', 'SCALE_5', 'SCALE_10', 'MULTIPLE_RATING'].includes((question_type || '').toUpperCase()) && stats.scaleStats
    ) {
      const s = stats.scaleStats;
      prompt += `Rating Statistics:\n`;
      prompt += `- Total Responses: ${s.totalResponses ?? 'N/A'}\n`;
      prompt += `- Average Rating: ${s.average ?? 'N/A'}\n`;
      prompt += `- Median Rating: ${s.median ?? 'N/A'}\n`;
      prompt += `- Mode (Most Common) Rating: ${s.mode ?? 'N/A'}\n`;
      prompt += `- Score Range: ${s.min ?? 'N/A'} - ${s.max ?? 'N/A'}\n`;
      if (s.standardDeviation !== undefined) prompt += `- Standard Deviation: ${s.standardDeviation}\n`;
      if (s.distribution) {
        const dist = Object.entries(s.distribution).map(([k, v]) => `${k}: ${v}`).join(', ');
        prompt += `- Distribution: ${dist}\n`;
      }
      prompt += '\n';
    }
    // STACK RANK
    if ((question_type || '').toUpperCase() === 'STACK_RANK' && stats.stackRankStats) {
      const s = stats.stackRankStats;
      prompt += `Ranking Statistics:\n`;
      prompt += `- Total Responses: ${s.totalResponses ?? 'N/A'}\n`;
      if (s.options && s.options.length > 0) {
        prompt += `- Rankings per Option:\n`;
        s.options.forEach(opt => {
          const ranks = Object.entries(opt.rankings).map(([pos, cnt]) => `${pos}: ${cnt}`).join(', ');
          prompt += `  - ${opt.option}: ${ranks}\n`;
        });
      }
      prompt += '\n';
    }
    // MULTIPLE CHOICE
    if ((question_type || '').toUpperCase() === 'MULTIPLE_CHOICE' && stats.multipleChoiceStats) {
      const s = stats.multipleChoiceStats;
      prompt += `Option Statistics:\n`;
      prompt += `- Total Responses: ${s.totalResponses ?? 'N/A'}\n`;
      if (s.options && s.options.length > 0) {
        prompt += `- Option Distribution:\n`;
        s.options.forEach(opt => {
          prompt += `  - ${opt.option}: ${opt.count} (${opt.percentage}%)\n`;
        });
      }
      prompt += '\n';
    }
    // BINARY
    if ((question_type || '').toUpperCase() === 'BINARY' && stats.binaryStats) {
      const s = stats.binaryStats;
      prompt += `Binary Statistics:\n`;
      prompt += `- Yes: ${s.yes ?? 'N/A'} (${s.yesPercentage ?? 'N/A'}%)\n`;
      prompt += `- No: ${s.no ?? 'N/A'} (${s.noPercentage ?? 'N/A'}%)\n`;
      prompt += `- Total Responses: ${s.totalResponses ?? 'N/A'}\n`;
      prompt += '\n';
    }
    // Participation/anonymous (for all types)
    prompt += `Participation Statistics:\n`;
    prompt += `- Total Responses: ${stats.total_responses ?? stats.totalResponses ?? 'N/A'}\n`;
    prompt += `- Participation Rate: ${stats.participation_rate ?? stats.participationRate ?? 'N/A'}\n`;
    prompt += `- Anonymous Responses: ${stats.anonymous_count ?? stats.anonymousCount ?? 'N/A'}\n`;
    prompt += '\n';
  }

  // Add question type specific instructions
  switch (question_type?.toLowerCase()) {
    case 'open_ended':
    case 'default':
      prompt += `This is an open-ended question. Please analyze for:\n`;
      prompt += `- Common themes and patterns\n`;
      prompt += `- Sentiment indicators\n`;
      prompt += `- Actionable suggestions\n`;
      prompt += `- Areas of consensus\n\n`;
      break;

    case 'binary':
      prompt += `This is a yes/no question. Please analyze for:\n`;
      prompt += `- Overall distribution of responses\n`;
      prompt += `- Key reasons for each choice\n`;
      prompt += `- Patterns in the explanations\n\n`;
      break;

    case 'multiple_choice':
      prompt += `This is a multiple choice question. Please analyze for:\n`;
      prompt += `- Distribution of selected options\n`;
      prompt += `- Patterns in option preferences\n`;
      prompt += `- Common explanations for choices\n\n`;
      break;

    case 'multiple_rating':
    case 'scale':
    case 'scale_5':
    case 'scale_10':
      prompt += `This is a rating scale question. Please analyze for:\n`;
      prompt += `- Average ratings and distribution\n`;
      prompt += `- Reasons for high/low ratings\n`;
      prompt += `- Trends across different ratings\n\n`;
      break;

    case 'stack_rank':
      prompt += `This is a ranking question. Please analyze for:\n`;
      prompt += `- Most common ranking patterns\n`;
      prompt += `- Top and bottom ranked items\n`;
      prompt += `- Reasoning behind rankings\n\n`;
      break;
  }

  // Add session type specific instructions
  switch (session_type?.toLowerCase()) {
    case 'crowdsourcing':
      prompt += `For this crowdsourcing session, focus on:\n`;
      prompt += `- Innovative ideas and suggestions\n`;
      prompt += `- Common pain points and solutions\n`;
      prompt += `- Areas of consensus\n\n`;
      break;

    case 'prioritize':
      prompt += `For this prioritization session, focus on:\n`;
      prompt += `- Clear priorities and rationale\n`;
      prompt += `- Resource allocation insights\n`;
      prompt += `- Implementation recommendations\n\n`;
      break;

    case 'survey':
      prompt += `For this survey, focus on:\n`;
      prompt += `- Key trends and patterns\n`;
      prompt += `- Actionable feedback\n`;
      prompt += `- Areas needing attention\n\n`;
      break;
  }

  // Add theme-specific analysis instructions
  prompt += `Please analyze the responses through these lenses:\n`;
  prompt += `- Leadership & Culture: Value alignment, communication effectiveness\n`;
  prompt += `- Operational Efficiency: Process improvements, resource allocation\n`;
  prompt += `- Employee Engagement: Recognition patterns, career development\n`;
  prompt += `- Inclusivity & Diversity: Trust indicators, perspective integration\n\n`;

  // --- Add anti-low-engagement instruction ---
  prompt += `IMPORTANT: Do not highlight \"Low Engagement\" as a key theme, even if participation is low.\n\n`;

  // Add response data with size monitoring
  prompt += `Here are the responses to analyze:\n\n`;
  let totalLength = prompt.length;
  
  posts.slice(0, MAX_POSTS_PER_BATCH).forEach((post, index) => {
    const postContent = `Response ${index + 1}: ${post.content}\n`;
    if (totalLength + postContent.length > 8000) {
      console.warn('Prompt size limit reached, truncating responses');
      return;
    }
    prompt += postContent;
    totalLength += postContent.length;

    if (post.children?.length > 0) {
      post.children.forEach(comment => {
        const commentContent = `- Comment: ${comment.content}\n`;
        if (totalLength + commentContent.length <= 8000) {
          prompt += commentContent;
          totalLength += commentContent.length;
        }
      });
    }
    prompt += '\n';
  });

  console.log('Prompt generated:', { 
    length: prompt.length,
    truncated: posts.length > MAX_POSTS_PER_BATCH
  });

  return prompt;
}

export default AIApi;